/**
 * The Standards page's load and save pipeline (firm-model spec §6.2): the firm record, the
 * editable draft and its baseline, and a save that carries the loaded version so a
 * colleague's newer save is refused (`stale_version`) rather than lost. The page stays
 * wiring + JSX.
 */
import type { FirmStandardsRecord } from "@contract/standards/standards";
import * as React from "react";
import { track } from "../analytics/analytics";
import { ApiError } from "../api/client";
import type * as standardsApi from "../api/standards";
import { fromDraft, toDraft, type StandardsDraft } from "./standardsModel";

type Api = typeof standardsApi;

type Loaded = { record: FirmStandardsRecord; baseline: StandardsDraft; draft: StandardsDraft };

type SaveError = { message: string; stale: boolean };

/** A load failure as copy. A 404 means the panel shipped before the API (deploy skew). */
function loadErrorCopy(e: unknown): string {
  if (e instanceof ApiError && e.status === 404) return "Standards aren't available yet. Try again in a few minutes.";
  return e instanceof ApiError ? e.message : "We couldn't load your standards. Try again.";
}

export function useFirmStandards(api: Api, onSaved: () => void) {
  const [loaded, setLoaded] = React.useState<Loaded | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<SaveError | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [savedNote, setSavedNote] = React.useState(false);
  const [nonce, setNonce] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    api
      .fetchFirmStandards()
      .then((record) => {
        if (!alive) return;
        const draft = toDraft(record);
        setLoaded({ record, baseline: draft, draft });
        setLoadError(null);
      })
      .catch((e: unknown) => alive && setLoadError(loadErrorCopy(e)));
    return () => {
      alive = false;
    };
  }, [api, nonce]);

  const patch = (partial: Partial<StandardsDraft>) => {
    setSavedNote(false);
    setLoaded((prev) => (prev ? { ...prev, draft: { ...prev.draft, ...partial } } : prev));
  };

  const discard = () => {
    if (loaded) patch(loaded.baseline);
  };

  /** Drops the draft and loads the latest record (after a `stale_version` refusal). */
  const reload = () => {
    setSaveError(null);
    setSavedNote(false);
    // Back to the Loader: the stale draft can't be saved (or overwrite the refetch) meanwhile.
    setLoaded(null);
    setNonce((n) => n + 1);
  };

  const save = async () => {
    if (!loaded) return;
    setSaving(true);
    setSaveError(null);
    try {
      const saved = await api.saveFirmStandards({ ...fromDraft(loaded.draft), version: loaded.record.version });
      const next = toDraft(saved);
      setLoaded({ record: saved, baseline: next, draft: next });
      setSavedNote(true);
      track("web_standards_saved", { version: saved.version });
      onSaved();
    } catch (e) {
      setSaveError({
        message: e instanceof ApiError ? e.message : "We couldn't save your standards. Try again.",
        stale: e instanceof ApiError && e.code === "stale_version",
      });
    } finally {
      setSaving(false);
    }
  };

  return {
    loaded,
    loadError,
    saveError,
    clearSaveError: () => setSaveError(null),
    saving,
    savedNote,
    patch,
    discard,
    reload,
    save,
  };
}
